/* eslint-disable max-len */
import { QueryClient } from 'react-query';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            /**
             * Set this to `false` to disable automatic refetching when the query mounts or changes query keys.
             * To refetch the query, use the `refetch` method returned from the `useQuery` instance.
             * Defaults to `true`.
             */
            // enabled: false,
            /**
             * The time in milliseconds after data is considered stale.
             * If set to `Infinity`, the data will never be considered stale.
             */
            // staleTime?: number;
            /**
             * If set to a number, the query will continuously refetch at this frequency in milliseconds.
             * Defaults to `false`.
             */
            // refetchInterval?: number | false;
            /**
             * If set to `true`, the query will continue to refetch while their tab/window is in the background.
             * Defaults to `false`.
             */
            // refetchIntervalInBackground?: boolean;
            /**
             * If set to `true`, the query will refetch on window focus if the data is stale.
             * If set to `false`, the query will not refetch on window focus.
             * If set to `'always'`, the query will always refetch on window focus.
             * Defaults to `true`.
             */
            refetchOnWindowFocus: false,
            /**
             * If set to `true`, the query will refetch on reconnect if the data is stale.
             * If set to `false`, the query will not refetch on reconnect.
             * If set to `'always'`, the query will always refetch on reconnect.
             * Defaults to `true`.
             */
            // refetchOnReconnect?: boolean | 'always';
            /**
             * If set to `true`, the query will refetch on mount if the data is stale.
             * If set to `false`, will disable additional instances of a query to trigger background refetches.
             * If set to `'always'`, the query will always refetch on mount.
             * Defaults to `true`.
             */
            // refetchOnMount: false,
            /**
             * If set to `false`, the query will not be retried on mount if it contains an error.
             * Defaults to `true`.
             */
            retryOnMount: false,
            /**
             * If set, the component will only re-render if any of the listed properties change.
             * When set to `['data', 'error']`, the component will only re-render when the `data` or `error` properties change.
             * When set to `tracked`, access to properties will be tracked, and the component will only re-render when one of the tracked properties change.
             */
            // notifyOnChangeProps?: Array<keyof InfiniteQueryObserverResult> | 'tracked';
            /**
             * If set, the component will not re-render if any of the listed properties change.
             */
            // notifyOnChangePropsExclusions?: Array<keyof InfiniteQueryObserverResult>;
            /**
             * This callback will fire any time the query successfully fetches new data.
             */
            // onSuccess?: (data: TData) => void;
            /**
             * This callback will fire if the query encounters an error and will be passed the error.
             */
            // onError?: (err: TError) => void;
            /**
             * This callback will fire any time the query is either successfully fetched or errors and be passed either the data or error.
             */
            // onSettled?: (data: TData | undefined, error: TError | null) => void;
            /**
             * Whether errors should be thrown instead of setting the `error` property.
             * Defaults to `false`.
             */
            // useErrorBoundary?: boolean;
            /**
             * This option can be used to transform or select a part of the data returned by the query function.
             */
            // select?: (data: TQueryData) => TData;
            /**
             * If set to `true`, the query will suspend when `status === 'loading'`
             * and throw errors when `status === 'error'`.
             * Defaults to `false`.
             */
            // suspense?: boolean;
            /**
             * Set this to `true` to keep the previous `data` when fetching based on a new query key.
             * Defaults to `false`.
             */
            keepPreviousData: true,
            /**
             * If set, this value will be used as the placeholder data for this particular query observer while the query is still in the `loading` data and no initialData has been provided.
             */
            // placeholderData?: TQueryData | PlaceholderDataFunction<TQueryData>;
            /**
             * If set, the observer will optimistically set the result in fetching state before the query has actually started fetching.
             * This is to make sure the results are not lagging behind.
             * Defaults to `true`.
             */
            // optimisticResults?: boolean;
        },
    },
});

export { queryClient };
